import { createFileRoute } from "@tanstack/react-router";
import { Users, Calendar, DollarSign, FileCheck, Briefcase, TrendingUp } from "lucide-react";
import { ServicePage } from "@/components/sections/service-page";

export const Route = createFileRoute("/software/hrm-payroll")({
  component: () => <ServicePage data={data} />,
  head: () => ({
    meta: [
      { title: "HRM & Payroll Software — CoderLity" },
      { name: "description", content: "People operations from hiring to payroll — applicant tracking, onboarding, time-off, performance and compliant payroll." },
      { property: "og:title", content: "HRM & Payroll Software — CoderLity" },
      { property: "og:description", content: "Run hiring, onboarding, time-off, performance and payroll on one HR platform." },
      { property: "og:url", content: "/software/hrm-payroll" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/software/hrm-payroll" }],
  }),
});

const data = {
  eyebrow: "Software",
  title: "HRM & Payroll",
  sub: "People operations, hiring to payroll.",
  intro:
    "Stop running HR on spreadsheets and email threads. Our HRM platform covers the full employee lifecycle — applicant tracking, onboarding, employee records, time-off, performance reviews, expense claims and fully-compliant payroll. Built for teams of 10 to 10,000 with role-based permissions and audit-ready logs.",
  features: [
    { icon: Briefcase, title: "Applicant tracking", description: "Job postings, candidate pipelines, interview scorecards and offers." },
    { icon: FileCheck, title: "Onboarding & docs", description: "E-sign offer letters, collect docs and run new-hire checklists." },
    { icon: Users, title: "Employee records", description: "Single source of truth for every employee — secure and audit-ready." },
    { icon: Calendar, title: "Time-off & attendance", description: "PTO requests, approvals, accruals and clock-in/out tracking." },
    { icon: TrendingUp, title: "Performance reviews", description: "Goals, 360 reviews, 1:1 templates and continuous feedback." },
    { icon: DollarSign, title: "Compliant payroll", description: "Automated payroll with tax filing, payslips and direct deposit." },
  ],
  process: [
    { step: "01", title: "Discovery", description: "Map your current HR processes, policies and compliance needs." },
    { step: "02", title: "Configure", description: "Set up org chart, leave policies, payroll rules and approvals." },
    { step: "03", title: "Migrate", description: "Import employees, history and prior payroll runs." },
    { step: "04", title: "Launch & train", description: "Manager + employee training and parallel payroll runs." },
  ],
  deliverables: [
    "Configured HRM platform with org structure",
    "Custom leave and time-off policies",
    "Compliant payroll setup with tax filing",
    "Onboarding templates and e-sign documents",
    "Performance review and goal frameworks",
    "Manager and employee training",
  ],
  techStack: ["Custom HRM (React + Node)", "BambooHR", "Gusto", "Deel", "Rippling APIs", "Supabase", "DocuSign"],
  pricing: [
    { name: "Core HR", price: "$5", period: "employee / month", features: ["Employee records", "Time-off tracking", "Onboarding", "Basic reporting"] },
    { name: "HR + Payroll", price: "$12", period: "employee / month", highlighted: true, features: ["Everything in Core", "Full payroll + tax filing", "Performance reviews", "Expense management"] },
    { name: "Enterprise", price: "Custom", features: ["Multi-country payroll", "Custom integrations", "Dedicated CSM", "SLA + compliance support"] },
  ],
  faqs: [
    { q: "Which countries does payroll support?", a: "We support multi-country payroll across US, UK, EU, India, Bangladesh, UAE and more — with local tax compliance built in." },
    { q: "Can we keep our existing payroll provider?", a: "Yes — we integrate with Gusto, Rippling, Deel, ADP and others if you only want HRM features." },
    { q: "Is it GDPR / SOC 2 compliant?", a: "Yes. Role-based access, audit logs, data residency options and signed BAA / DPAs available." },
    { q: "How fast can we go live?", a: "Core HR in 2 weeks. HR + payroll typically 4–6 weeks including parallel payroll runs and team training." },
  ],
};
